import React, { useState, useEffect, useCallback } from 'react';
import { Button, message, Modal, InputNumber, Spin, Empty } from 'antd';
import {
  WalletOutlined, InboxOutlined, AccountBookOutlined, ShopOutlined,
  ReloadOutlined, EditOutlined, FundOutlined,
} from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';

const INK = '#26201C';
const CORAL = '#D85A30';
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

// 完整精确数字（大数字用，不转「万」）
const fmtFull = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '0.00';
  const neg = n < 0;
  const abs = Math.abs(n).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (neg ? '-' : '') + abs;
};
// 「万」精简格式
const fmtWan = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '¥0';
  const neg = n < 0;
  const abs = Math.abs(n);
  return (neg ? '-¥' : '¥') + (abs >= 10000 ? (abs / 10000).toFixed(2) + '万' : abs.toLocaleString('zh-CN', { maximumFractionDigits: 2 }));
};

export default function Overview({ embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [openPlat, setOpenPlat] = useState(false);
  const [platVal, setPlatVal] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/overview').then(x => x.json());
      if (r.success) setData(r);
      else message.error(r.error || '加载失败');
    } catch (e) { message.error('经营总览加载失败：' + (e.message || '')); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitPlat = async () => {
    if (platVal == null || Number(platVal) < 0) return message.warning('请填写不小于 0 的金额');
    setSaving(true);
    try {
      const r = await apiFetch('/api/overview/platform-receivable', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: Number(platVal) }),
      }).then(x => x.json());
      if (r.success) { message.success('平台待结算已更新'); setOpenPlat(false); load(); }
      else message.error(r.error || '更新失败');
    } catch (e) { message.error('更新失败：' + e.message); }
    setSaving(false);
  };

  // 玻璃拟态公共样式（与全站「一层 0.42 色调1」统一）
  const glass = {
    background: 'rgba(255,255,255,0.42)',
    backdropFilter: 'blur(18px) saturate(1.7)', WebkitBackdropFilter: 'blur(18px) saturate(1.7)',
    border: '1px solid rgba(255,255,255,0.72)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.92), 0 6px 20px rgba(216,90,48,0.07)',
  };
  const coralIco = { background: 'linear-gradient(135deg, #D85A30, #F08A62)', color: '#fff', boxShadow: '0 4px 10px rgba(216,90,48,0.24)' };

  const cashTotal = data ? Number(data.cash.total) : 0;
  const invAmount = data ? Number(data.inventory.amount) : 0;
  const custReceivable = data ? Number(data.customer_receivable) : 0;
  const platReceivable = data ? Number(data.platform_receivable) : 0;
  const total = data ? Number(data.total_assets) : 0;
  const accounts = data && data.cash.accounts ? data.cash.accounts : [];

  const blocks = [
    { key: 'cash', label: '可支配现金', sub: '实时 · 资金中心', icon: <WalletOutlined />, color: CORAL, val: cashTotal, extra: accounts.length + ' 个账户' },
    { key: 'inv', label: '仓库货值', sub: '实时 · 成本价口径', icon: <InboxOutlined />, color: '#5B8FF9', val: invAmount, extra: data ? data.inventory.sku + ' SKU · ' + (data.inventory.stock || 0).toLocaleString() + ' 件' : '' },
    { key: 'cust', label: '客户欠款', sub: '自动 · 微信/客户未回款', icon: <AccountBookOutlined />, color: '#13C2C2', val: custReceivable, extra: '已排除平账单' },
    { key: 'plat', label: '平台待结算', sub: '手填 · 拼多多待提现', icon: <ShopOutlined />, color: '#FA8C16', val: platReceivable, extra: data && data.platform_receivable_month ? '记录月 ' + data.platform_receivable_month : '待填写' },
  ];
  const pct = (v) => (total > 0 ? Math.round((Number(v) / total) * 1000) / 10 : 0);

  // 环形图（conic-gradient）
  const donutStyle = total > 0 ? {
    background: `conic-gradient(
      ${CORAL} 0% ${pct(cashTotal)}%,
      #5B8FF9 ${pct(cashTotal)}% ${pct(cashTotal) + pct(invAmount)}%,
      #13C2C2 ${pct(cashTotal) + pct(invAmount)}% ${pct(cashTotal) + pct(invAmount) + pct(custReceivable)}%,
      #FA8C16 ${pct(cashTotal) + pct(invAmount) + pct(custReceivable)}% 100%
    )`,
  } : { background: '#F1EDE8' };

  if (loading && !data) {
    return <div className="page-container" style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}><Spin size="large" /></div>;
  }

  return (
    <div className={embedded ? '' : 'page-container'}>
      {/* 标题栏（嵌入模式隐藏，由宿主页面提供标题） */}
      {!embedded && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: INK }}>
            <FundOutlined style={{ color: CORAL, marginRight: 8 }} />经营总览
          </div>
          <div style={{ fontSize: 12, color: '#A08C82', marginTop: 4 }}>
            一眼看清家底：可支配现金 + 仓库货值 + 客户欠款 + 平台待结算　|　实时数据，平台待结算可手动更新
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
      </div>
      )}

      {/* 总家底横幅 */}
      <div style={{
        position: 'relative', overflow: 'hidden', borderRadius: 20, padding: '26px 28px', marginBottom: 16,
        ...glass, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.95), 0 14px 40px rgba(216,90,48,0.14)',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(115deg, rgba(216,90,48,0.13) 0%, rgba(232,116,78,0.05) 48%, rgba(240,138,98,0) 75%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', right: -70, top: -70, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(240,138,98,0.30) 0%, rgba(240,138,98,0) 70%)', filter: 'blur(18px)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 28 }}>
          {/* 左：大数字 + 构成 */}
          <div style={{ flex: '1 1 300px', minWidth: 260 }}>
            <div style={{ fontSize: 13, color: '#5F5E5A', display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: CORAL, boxShadow: '0 0 0 3px rgba(216,90,48,0.16)' }} />
              总家底（钱 + 货 + 应收）
            </div>
            <div style={{ fontSize: 52, fontWeight: 700, marginTop: 8, letterSpacing: .5, lineHeight: 1.04, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif', color: INK }}>
              ¥ {fmtFull(total)}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 26px', marginTop: 18, paddingTop: 16, borderTop: '1px solid rgba(216,90,48,0.12)' }}>
              {blocks.map(b => (
                <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: b.color, flex: 'none' }} />
                  <span style={{ fontSize: 12, color: '#A08C82' }}>{b.label}</span>
                  <span style={{ fontSize: 14, color: INK, fontWeight: 600, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtWan(b.val)}</span>
                </div>
              ))}
            </div>
          </div>
          {/* 右：环形图 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flex: 'none' }}>
            <div style={{ width: 150, height: 150, borderRadius: '50%', position: 'relative', ...donutStyle, boxShadow: '0 8px 24px rgba(38,32,28,0.12)' }}>
              <div style={{ position: 'absolute', inset: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontSize: 11, color: '#A08C82' }}>总家底</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: INK, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif', marginTop: 2 }}>{fmtWan(total)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 四块构成卡片 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 13, marginBottom: 16 }}>
        {blocks.map(b => {
          const isPlat = b.key === 'plat';
          return (
            <div key={b.key} className="kpi-tap" style={{
              flex: '1 1 200px', maxWidth: 'calc(25% - 10px)', minWidth: 185, borderRadius: 16, padding: '16px', overflow: 'hidden', position: 'relative',
              ...glass, transition: 'transform .08s ease, box-shadow .2s ease', cursor: 'default',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flex: 'none', color: '#fff', background: `linear-gradient(135deg, ${b.color}, ${b.color}CC)`, boxShadow: `0 4px 10px ${b.color}33` }}>{b.icon}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: INK }}>{b.label}</div>
                  <div style={{ fontSize: 10.5, color: '#A08C82' }}>{b.sub}</div>
                </div>
                {isPlat && (
                  <Button size="small" type="text" icon={<EditOutlined />} onClick={() => { setPlatVal(round2(platReceivable)); setOpenPlat(true); }}
                    style={{ marginLeft: 'auto', color: b.color, fontSize: 12, height: 24, padding: '0 6px' }} />
                )}
              </div>
              <div style={{ fontSize: 23, fontWeight: 700, color: INK, marginTop: 13, letterSpacing: .3, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif', whiteSpace: 'nowrap' }}>¥ {fmtFull(b.val)}</div>
              <div style={{ height: 4, borderRadius: 99, background: 'rgba(38,32,28,0.08)', marginTop: 12, overflow: 'hidden' }}>
                <div style={{ width: pct(b.val) + '%', height: '100%', borderRadius: 99, background: `linear-gradient(90deg, ${b.color}, ${b.color}CC)` }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 7, fontSize: 11, color: '#A08C82' }}>
                <span>{b.extra}</span>
                <span style={{ fontWeight: 600, color: b.color }}>{pct(b.val)}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* 资金明细（账户卡片） */}
      <div style={{ borderRadius: 16, padding: '16px 18px', marginBottom: 16, ...glass }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: INK, marginBottom: 12 }}>
          <span style={{ width: 26, height: 26, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flex: 'none', ...coralIco }}><WalletOutlined /></span>资金明细（可支配现金拆解）
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {accounts.map(a => (
            <div key={a.id} style={{
              flex: '1 1 150px', minWidth: 140, borderRadius: 12, padding: '12px 14px',
              background: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.72)',
            }}>
              <div style={{ fontSize: 12.5, color: '#5F5E5A', fontWeight: 600, marginBottom: 5 }}>{a.name}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: Number(a.balance) < 0 ? '#CF1322' : INK, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>
                {Number(a.balance) < 0 ? '-¥ ' : '¥ '}{fmtFull(Math.abs(a.balance))}
              </div>
            </div>
          ))}
          {accounts.length === 0 && <Empty description="暂无资金账户" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        </div>
      </div>

      {/* 口径说明 */}
      <div style={{ borderRadius: 16, padding: '14px 18px', marginBottom: 16, ...glass }}>
        <div style={{ fontSize: 12, color: '#A08C82', lineHeight: 1.9 }}>
          <b style={{ color: '#5F5E5A' }}>口径说明：</b>总家底 = 可支配现金（资金中心实时）+ 仓库货值（库存 × 成本价，实时）+ 客户欠款（微信/客户订单未回款，自动）+ 平台待结算（拼多多后台待提现款，手填）。
          其中「平台待结算」目前需手动更新，待采集器/拼多多接口对接后自动同步。
        </div>
      </div>

      {/* 编辑平台待结算 */}
      <Modal title="更新平台待结算款" open={openPlat} onCancel={() => setOpenPlat(false)} onOk={submitPlat} okText="保存" cancelText="取消" confirmLoading={saving} destroyOnClose>
        <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 6 }}>拼多多后台当前待提现余额（元）</div>
        <InputNumber style={{ width: '100%' }} min={0} precision={2} value={platVal} onChange={v => setPlatVal(v)} placeholder="如 76781.17" />
        <div style={{ fontSize: 12, color: '#A08C82', marginTop: 8 }}>此数字计入「总家底」，提现到银行卡后请同步更新。</div>
      </Modal>
    </div>
  );
}
